import { useState, useMemo, useCallback } from "react";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";
import { Search, Plus, Copy, Trash2, RotateCcwKey } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import ApiKeySuccessDialog from "../components/ApiKeySuccessDialog";
import DataTable from "../components/DataTable";
import CreateApiKeyDialog from "../components/CreateApiKeyDialog";
import { Spinner } from "../components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  useApiKeys,
  useDeleteApiKey,
  useRotateApiKey,
} from "../hooks/useApiKeys";
import type { ApiKey, ApiKeyWithValue } from "../services/api-keys-rag-service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useProjects } from "../contexts/ProjectContext";

// For formatting dates
const formatDate = (
  dateString: string | null | undefined,
  neverLabel = "Never",
) => {
  if (!dateString) return neverLabel;
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const isApiKeyExpired = (apiKey: ApiKey) => {
  if (!apiKey.expires_at) return false;

  const expiresAt = new Date(apiKey.expires_at).getTime();
  return !Number.isNaN(expiresAt) && expiresAt <= Date.now();
};

const ApiKeys = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "rotate";
    apiKey: ApiKey;
  } | null>(null);
  const [rotatedApiKey, setRotatedApiKey] = useState<ApiKeyWithValue | null>(
    null,
  );

  const { selectedProjects, availableProjects } = useProjects();
  // When "All Projects" is selected, send every available project id
  const projectIds =
    Array.isArray(selectedProjects) && !selectedProjects.includes("all")
      ? selectedProjects
      : availableProjects.map((project: { id: string }) => project.id);

  const {
    data: apiKeysResponse,
    isLoading,
    error,
    isFetching,
  } = useApiKeys({
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    projectIds,
  });
  const deleteMutation = useDeleteApiKey();
  const rotateMutation = useRotateApiKey();
  const apiKeys = apiKeysResponse?.items ?? [];
  const totalItems = apiKeysResponse?.metadata.total_items ?? 0;

  const handleKeyCreated = useCallback(() => {
    // Rely on React Query's onSuccess in the hook to invalidate
  }, []);

  const handleCopyText = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      toast.success(t("common.copiedToClipboard"));
    },
    [t],
  );

  const handleDelete = useCallback((apiKey: ApiKey) => {
    setConfirmAction({ type: "delete", apiKey });
  }, []);

  const handleRotate = useCallback((apiKey: ApiKey) => {
    setConfirmAction({ type: "rotate", apiKey });
  }, []);

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction) {
      return;
    }

    const closeDialog = () => setConfirmAction(null);

    if (confirmAction.type === "delete") {
      deleteMutation.mutate(confirmAction.apiKey.id, {
        onSuccess: () => {
          closeDialog();
        },
      });
      return;
    }

    if (confirmAction.type === "rotate") {
      rotateMutation.mutate(confirmAction.apiKey.id, {
        onSuccess: (rotatedKey) => {
          setRotatedApiKey(rotatedKey.new_key);
          closeDialog();
        },
      });
      return;
    }
  }, [confirmAction, deleteMutation, rotateMutation]);

  const isConfirmingAction =
    deleteMutation.isPending || rotateMutation.isPending;
  const pendingDeleteId = deleteMutation.isPending
    ? deleteMutation.variables
    : null;
  const pendingRotateId = rotateMutation.isPending
    ? rotateMutation.variables
    : null;
  const isDeleteConfirmation = confirmAction?.type === "delete";
  const isRotateConfirmation = confirmAction?.type === "rotate";
  const confirmActionLabel = isDeleteConfirmation
    ? t("apiKeys.deleteKey")
    : t("apiKeys.rotateKey");
  const pendingConfirmActionLabel = isDeleteConfirmation
    ? t("common.deleting")
    : t("common.rotating");

  const filteredData = useMemo(() => {
    return apiKeys.filter((key) => {
      const query = searchQuery.toLowerCase();
      return (
        !searchQuery ||
        key.name.toLowerCase().includes(query) ||
        key.id.toLowerCase().includes(query) ||
        key.key?.toLowerCase().includes(query) ||
        key.project_name?.toLowerCase().includes(query) ||
        key.project_id?.toLowerCase().includes(query)
      );
    });
  }, [searchQuery, apiKeys]);

  const columns: ColumnDef<ApiKey>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("apiKeys.keyName"),
        minSize: 200,
        cell: ({ row }) => (
          <div>
            <span className="text-sm font-medium text-foreground">
              {row.original.name}
            </span>
            <span className="block font-mono text-[0.72rem] text-muted-foreground/70">
              {row.original.id}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "key",
        header: t("apiKeys.secretKey"),
        minSize: 140,
        cell: ({ row }) => (
          <span
            className={
              row.original.key
                ? "font-mono text-[0.82rem] text-foreground"
                : "text-sm italic text-muted-foreground"
            }
          >
            {row.original.key || t("common.notAvailable")}
          </span>
        ),
      },
      {
        accessorKey: "project_name",
        header: t("apiKeys.projectName"),
        minSize: 200,
        cell: ({ row }) => (
          <span
            className={
              row.original.project_name
                ? "text-[0.82rem] text-foreground"
                : "text-sm italic text-muted-foreground"
            }
          >
            {row.original.project_name || t("common.notAvailable")}
          </span>
        ),
      },
      {
        accessorKey: "expires_at",
        header: t("apiKeys.expires"),
        minSize: 140,
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId) as string | null;
          const b = rowB.getValue(columnId) as string | null;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        cell: ({ row }) => (
          <span
            className={
              !row.original.expires_at
                ? "text-sm italic text-muted-foreground"
                : "text-sm text-foreground"
            }
          >
            {formatDate(row.original.expires_at, t("common.never"))}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: t("common.created"),
        minSize: 140,
        cell: ({ row }) => (
          <div>
            <span className="block text-[0.82rem] text-foreground">
              {formatDate(row.original.created_at)}
            </span>
          </div>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        minSize: 100,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyText(row.original.id);
              }}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-primary"
              title={t("apiKeys.copyId")}
            >
              <Copy className="size-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRotate(row.original);
              }}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted-foreground"
              title={t("apiKeys.rotateKey")}
              disabled={!row.original.is_active || rotateMutation.isPending}
              aria-busy={pendingRotateId === row.original.id}
            >
              {pendingRotateId === row.original.id ? (
                <Spinner className="size-4" />
              ) : (
                <RotateCcwKey className="size-4" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original);
              }}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
              title={t("apiKeys.deleteKey")}
              disabled={isConfirmingAction}
              aria-busy={pendingDeleteId === row.original.id}
            >
              {pendingDeleteId === row.original.id ? (
                <Spinner className="size-4" />
              ) : (
                <Trash2 className="size-4" />
              )}
            </button>
          </div>
        ),
      },
    ],
    [
      handleCopyText,
      handleDelete,
      handleRotate,
      isConfirmingAction,
      pendingRotateId,
      rotateMutation.isPending,
      t,
    ],
  );

  return (
    <DashboardLayout title={t("apiKeys.title")}>
      <div className="p-2 sm:p-4">
        {error && (
          <div className="mb-4 rounded-md bg-destructive/15 p-3 text-sm text-destructive">
            {t("apiKeys.failedToLoad")}
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative flex min-w-[250px] flex-1 items-center md:flex-initial">
            <Search className="absolute left-3 rtl:left-auto rtl:right-3 size-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t("apiKeys.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-full rounded-[8px] border border-border bg-card pl-9 pr-3 rtl:pl-3 rtl:pr-9 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
            />
          </div>

          <div className="flex-1" />

          <button
            onClick={() => setCreateDialogOpen(true)}
            className="flex h-10 items-center gap-2 rounded-[8px] bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-all hover:shadow-[0_4px_12px_rgba(19,211,158,0.3)] hover:brightness-110"
          >
            <Plus className="size-4" />
            {t("apiKeys.createNewKey")}
          </button>
        </div>

        <DataTable<ApiKey>
          columns={columns}
          data={filteredData}
          totalCount={totalItems}
          loading={isLoading || isFetching}
          pagination={pagination}
          onPaginationChange={setPagination}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage={t("apiKeys.noKeysFound")}
          getRowClassName={(apiKey) =>
            isApiKeyExpired(apiKey)
              ? "bg-muted/45 text-muted-foreground opacity-70 hover:bg-muted/55 dark:bg-muted/20 dark:opacity-60 dark:hover:bg-muted/25 [&_td]:text-muted-foreground [&_span]:text-muted-foreground [&_button]:text-muted-foreground/70"
              : undefined
          }
        />

        <CreateApiKeyDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onKeyCreated={handleKeyCreated}
        />

        <AlertDialog
          open={confirmAction !== null}
          onOpenChange={(open) => {
            if (!open && !isConfirmingAction) {
              setConfirmAction(null);
            }
          }}
        >
          <AlertDialogContent className="border-border bg-card">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-foreground">
                {isRotateConfirmation
                  ? t("apiKeys.rotateApiKey")
                  : t("apiKeys.deleteApiKey")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isRotateConfirmation
                  ? t("apiKeys.rotateConfirm", {
                      name: confirmAction.apiKey.name,
                    })
                  : t("apiKeys.deleteConfirm", {
                      name: confirmAction?.apiKey.name,
                    })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="border-border bg-card text-foreground hover:bg-accent"
                disabled={isConfirmingAction}
              >
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmAction}
                className={
                  confirmAction?.type === "delete"
                    ? "bg-destructive text-white hover:bg-destructive/90"
                    : "bg-primary text-primary-foreground hover:bg-primary/90"
                }
                disabled={isConfirmingAction}
                aria-busy={isConfirmingAction}
              >
                {isConfirmingAction ? (
                  <>
                    <Spinner className="size-4" />
                    <span>{pendingConfirmActionLabel}</span>
                  </>
                ) : (
                  confirmActionLabel
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ApiKeySuccessDialog
          open={rotatedApiKey !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRotatedApiKey(null);
            }
          }}
          title={t("apiKeys.keyRotated")}
          apiKey={rotatedApiKey}
          note={t("apiKeys.saveKeyNote")}
        />
      </div>
    </DashboardLayout>
  );
};

export default ApiKeys;
